import React, { useState, useEffect, useRef } from 'react';

import CssBaseline from '@material-ui/core/CssBaseline';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Collapse from '@material-ui/core/Collapse';
import Divider from '@material-ui/core/Divider';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import LinearProgress from '@material-ui/core/LinearProgress';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Paper from '@material-ui/core/Paper';
import RefreshIcon from '@material-ui/icons/Refresh';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import UnfoldLessIcon from '@material-ui/icons/UnfoldLess';
import UnfoldMoreIcon from '@material-ui/icons/UnfoldMore';

import { makeStyles } from '@material-ui/core/styles';

import CancelIcon from '@material-ui/icons/Cancel';
import { DataGrid } from '@mui/x-data-grid';

import AppContainer from '../Components/AppContainer';
import MadeWithLove from '../Components/MadeWithLove';

import InternalApi from '../Utils/InternalApi';
import Networker from '../Utils/Networker';
import withStyle from '../style';

// Object flatten function taken from `backend/utils/index.js`
function flatten(obj, separator = '.', array = false, depth = 50) {
    const recurse = (obj, prefix, depth, res) => {
        Object.keys(obj).forEach((key) => {
            const val = obj[key];

            if (val && (array || !Array.isArray(val)) && typeof(val) === 'object') {
                if (!depth) {
                    throw new Error('Exceeded maximum depth while attempting to flatten object!');
                }

                recurse(val, prefix + key + separator, depth - 1, res);
            } else {
                res[prefix + key] = val;
            }
        });

        return res;
    };

    return recurse(obj, '', depth, {});
};

// The grid for measurements uses this manual order for column sorting.
const MEASUREMENT_ORDER = [
    'id',
    '_id',
    'measurementTimeUTC',
    'mobileReceivedAtUTC',
    'measurementTimeZone',
    'measurementTimeZoneOffset',
    'measurementTimeLocal',
    'initialConnection',
    'userId',
    'userContainerId',
    'productId',
    'mac',
    'computedData',
    'hiddenComputedData',
    'data',
    'location',
    'meta',
    'payload'
];

// Get a sorted list of keys from a list of (flattened) measurements.
// Note: This function is very much specific to the measurement schema.
function sortedKeys(from, separator = '.') {
    const keys = new Set();

    // First, just build a key set which includes all of the possible fields.
    from.map((measurement) => Object.keys(measurement).forEach((key) => keys.add(key)));

    // Now, give back a sorted version of this list.
    const list = keys.values().toArray();

    // This sort is very specific to the underlying data layout of measurements.
    list.sort((a, b) => {
        const secondIndex = b.indexOf(separator);
        const firstIndex = a.indexOf(separator);

        // Grab the name of the first object component (before '.')
        const secondComponent = secondIndex < 0 ? b : b.substring(0, secondIndex);
        const firstComponent = firstIndex < 0 ? a : a.substring(0, firstIndex);

        // Find if these elements are in the manual order.
        const orderB = MEASUREMENT_ORDER.indexOf(secondComponent);
        const orderA = MEASUREMENT_ORDER.indexOf(firstComponent);

        if (orderA < 0 && orderB < 0) {
            // Here, neither string is in the manul order.
            // Use basic string ordering.
            return a < b ? 1 : -1;
        } else if (orderA < 0) {
            // Here, a is not in the manul order. Use b.
            return 1;
        } else if (orderB < 0) {
            // Here, b is not in the manul order. Use a.
            return -1;
        } else if (orderA === orderB) {
            // Here, the first component matches.
            // Use basic string ordering.
            return (a < b ? 1 : -1);
        } else {
            // Use manual ordering.
            return orderA - orderB;
        }
    });

    return list;
};

// Manual column info for measurement grid
const MEASUREMENT_COL_INFO = {
    'id':                           { width: 75 },
    '_id':                          { headerName: 'ID',              width: 220  },
    'measurementTimeUTC':           { headerName: 'Time (UTC)',      width: 200  },
    'measurementTimeLocal':         { headerName: 'Time (Local)',    width: 225  },
    'measurementTimeZoneOffset':    { headerName: 'Local Offset',    width: 160  },
    'mobileReceivedAtUTC':          { headerName: 'Time (Mobile)',   width: 225  },
    'measurementTimeZone':          { headerName: 'Time Zone',       width: 175  },
    'initialConnection':            { headerName: 'Initial',         width: 125  },
    'userId':                       { headerName: 'User ID',         width: 220  },
    'userContainerId':              { headerName: 'User Container',  width: 220  },
    'productId':                    { headerName: 'Product ID',      width: 220  },
    'mac':                          { headerName: 'Label',           width: 150  },
    'payload':                      { headerName: 'Payload',         width: 1000 },
    'computedData.offsetBand':      { headerName: 'Band Offset',     width: 175  },
    'computedData.needsRefill':     { headerName: 'Needs Refill',    width: 175  },
    'computedData.needsRefill_50':  { headerName: '50% Refill',      width: 175  },
    'computedData.refilled':        { headerName: 'Refilled',        width: 175  },
    'computedData.modelTime':       { headerName: 'Model Time',      width: 175  },
    'computedData.amountLeft':      { headerName: 'Amount Left',     width: 200  },
    'data.version':                 { headerName: 'Data Version',    width: 175  },
    'data.usagecount':              { headerName: 'Usage (c)ount',   width: 175  },
    'data.usageTime':               { headerName: 'Usage Time',      width: 175  },
    'data.usageCount':              { headerName: 'Usage Count',     width: 175  },
    'data.temperature':             { headerName: 'Temperature',     width: 175  },
    'data.shake':                   { headerName: 'Shake Count',     width: 175  },
    'data.sequenceNumber':          { headerName: 'Sequence Number', width: 200  },
    'data.resetSource':             { headerName: 'Reset Source',    width: 175  },
    'data.relativetime':            { headerName: 'Relative (t)ime', width: 175  },
    'data.relativeTime':            { headerName: 'Relative Time',   width: 175  },
    'data.pushButtonResult':        { headerName: 'Button Result',   width: 175  },
    'data.productId':               { headerName: 'Product Code',    width: 175  },
    'data.powerRailResult':         { headerName: 'Power Rail',      width: 175  },
    'data.pickedUpFlag':            { headerName: 'Picked Up',       width: 175  },
    'data.payload':                 { headerName: 'Payload',         width: 500  },
    'data.modeAngleY':              { headerName: 'Mode (Y)',        width: 175  },
    'data.measurementType':         { headerName: 'Type',            width: 175  },
    'data.measurementSize':         { headerName: 'Size',            width: 175  },
    'data.measurementId':           { headerName: 'Firmware ID',     width: 175  },
    'data.measurement':             { headerName: 'Payload',         width: 500  },
    'data.meanAngleY':              { headerName: 'Mean (Y)',        width: 175  },
    'data.maxAngleY':               { headerName: 'Max (Y)',         width: 175  },
    'data.maxAngVel':               { headerName: 'Velocity',        width: 175  },
    'data.mac':                     { headerName: 'MAC',             width: 175  },
    'data.heartbeatFrequency':      { headerName: 'Heartbeat Freq.', width: 200  },
    'data.heartbeatCount':          { headerName: 'Heartbeat Count', width: 200  },
    'data.heartbeat':               { headerName: 'Heartbeat',       width: 175  },
    'data.forced':                  { headerName: 'Forced',          width: 175  },
};

// Create field configuration for a given column name in measurements grid.
function columnInfo(key, separator = '.') {
    let manualInfo = MEASUREMENT_COL_INFO[key];

    // See if this is a nested field with info about the specific group
    if (!manualInfo)
    {
        const separatorIdx = key.indexOf(separator);

        // Grab just the first piece of the name here.
        const name = separatorIdx < 0 ? key : key.substring(0, separatorIdx);

        manualInfo = MEASUREMENT_COL_INFO[name];
    }

    return Object.assign({
        field: key,
        headerName: key,
        width: 300
    }, manualInfo || {});
};

// Wrap a promise in some boolean state.
function whileState(setFunc, promise)
{
    return new Promise((res, _) => {
        setFunc(true);
        res(promise);
    }).finally(() => setFunc(false));
}

function updateGroup({ api, requestFn, groupName, setGroup, setSelected, guard, endpoint, queryBuilder, setLoading }) {
    setSelected(null);
    setGroup([]);

    if (!guard) {
        return Promise.resolve();
    }

    if (!api) {
        return Promise.resolve();
    }

    const query = queryBuilder();

    console.info(`Find ${groupName} for`, query);

    whileState(
        setLoading,
        requestFn(endpoint, query).then((res) => {
            if (!res.ok)
            {
                window.alert(`An error occurred while looking up ${groupName} info! (error='${res.error}')`);
                return;
            }

            if (!res[groupName]?.length)
            {
                console.warn(`No ${groupName} found for`, query);
                return;
            }

            console.info(`Found ${groupName}:`, res[groupName]);
            setGroup(res[groupName]);
        }).catch((err) => {
            console.error(`${groupName}:`, err);
            window.alert('Encountered an error while communicating with the backend!');
        })
    );
}

const getSession = () => Networker.get({ root: 'user', inner: 'session', cache: true });

export default function UserInfo({ match }) {
    const [ userQuery, setUserQuery ] = useState({ email: '', userId: '' });
    const [ includeInvalid, setIncludeInvalid ] = useState(false); 
    const [ loading, setLoading ] = useState(false);
    const [ blocked, setBlocked ] = useState(true);

    const [ users, setUsers ] = useState([]);
    const [ user, setUser ] = useState(null);

    const [ products, setProducts ] = useState([]);
    const [ product, setProduct ] = useState(null);

    const [ labels, setLabels ] = useState([]);
    const [ label, setLabel ] = useState();

    // This is the list of columns used for the measurement grid.
    const [ columns, setColumns ] = useState([]);

    const [ measurements, setMeasurements ] = useState([]);
    const [ measurement, setMeasurement ] = useState(null);

    const [ notificationJobs, setNotificationJobs ] = useState([]);
    const [ notifications, setNotifications ] = useState([]);

    const [ surveys, setSurveys ] = useState([]);

    const [ measurementSectionOpen, setMeasurementSectionOpen ] = useState(true);
    const [ notificationJobsSectionOpen, setNotificationJobsSectionOpen ] = useState(true);
    const [ notificationsSectionOpen, setNotificationsSectionOpen ] = useState(true);
    const [ surveysSectionOpen, setSurveysSectionOpen ] = useState(true);

    const [ usersSectionOpen, setUsersSectionOpen ] = useState(true);
    const [ selectedUserSectionOpen, setSelectedUserSectionOpen ] = useState(true);
    const [ productsSectionOpen, setProductsSectionOpen ] = useState(true);
    const [ selectedProductSectionOpen, setSelectedProductSectionOpen ] = useState(true);
    const [ labelsSectionOpen, setLabelsSectionOpen ] = useState(true);

    const [ colorByMeasurementType, setColorByMeasurementType ] = useState(false);
    const [ selectedMeasurementRowId, setSelectedMeasurementRowId ] = useState(null);

    const [ openNotificationJobs, setOpenNotificationJobs ] = useState({});
    const [ openNotifications, setOpenNotifications ] = useState({});
    const [ openSurveys, setOpenSurveys ] = useState({});

    const [ retriggerOptionsByJob, setRetriggerOptionsByJob ] = useState({});
    const [ notificationJobActionLoading, setNotificationJobActionLoading ] = useState({});

    const [ socket, setSocket ] = useState(null);
    const [ socketReady, setSocketReady ] = useState(false);
    const api = useRef(null);

    // Check permissions + connect to socket.
    useEffect(() => {
        let cancelled = false;
        let activeSocket = null;

        whileState(setLoading, getSession().then(async (res) => {
            if (!res.body.isSuperUser)
            {
                window.alert('You don\'t have access to this page.');
                throw new Error('403');
            }

            api.current = new InternalApi();

            const ws = await InternalApi.connect();
            activeSocket = ws;

            if (cancelled) {
                await InternalApi.disconnect(ws).catch(() => {});
                return;
            }

            setSocket(ws);
            setBlocked(false);
            setSocketReady(true);

            api.current.socket = ws;
            api.current.connected = true;
        }).catch((err) => {
            console.error('Error:', err);

            if (err.message !== '403') {
                window.alert('Error connecting to backend.');
                setInterval(() => window.location.reload(), 3000);
            } else {
                // Redirect back to home
                window.location.href = '/admin';
            }
        }));

        return () => {
            cancelled = true;
            setSocketReady(false);
            setSocket(null);

            if (activeSocket) {
                InternalApi.disconnect(activeSocket).catch((err) => {
                    console.error('Socket disconnect error:', err);
                });
            }
        };
    }, []);
    
    const reconnectSocket = async () => {
        if (socket && socket.readyState === WebSocket.OPEN) {
            if (api.current) {
                api.current.socket = socket;
                api.current.connected = true;
            }

            setSocketReady(true);
            return socket;
        }

        setSocketReady(false);

        try {
            const newSocket = await InternalApi.connect();

            setSocket(newSocket);
            setSocketReady(true);

            if (api.current) {
                api.current.socket = newSocket;
                api.current.connected = true;
            }

            return newSocket;
        } catch (error) {
            console.error('Failed to reconnect to socket:', error);
            setSocketReady(false);
            throw new Error('WebSocket reconnection failed');
        }
    };

    const requestWithReconnect = async (endpoint, query, clientCallback) => {
        let activeSocket;

        try {
            activeSocket = await reconnectSocket();
        } catch (err) {
            console.error(err.message);
            throw err;
        }

        try {
            return await InternalApi.request(activeSocket, endpoint, query, clientCallback);
        } catch (err) {
            console.error(`Initial websocket request failed for ${endpoint}:`, err);

            activeSocket = await reconnectSocket();
            return await InternalApi.request(activeSocket, endpoint, query, clientCallback);
        }
    };

    // Search for users.
    const searchUsers = () => {
        updateGroup({
            api: api.current,
            requestFn: requestWithReconnect,
            groupName: 'users',
            setGroup: setUsers,
            setSelected: setUser,
            guard: socketReady,
            endpoint: '/user/find',
            queryBuilder: () => userQuery,
            setLoading: setLoading
        });
    };

    // Search products for a given user
    useEffect(() => {
        updateGroup({
            api: api.current,
            requestFn: requestWithReconnect,
            groupName: 'products',
            setGroup: setProducts,
            setSelected: setProduct,
            guard: socketReady && user?._id,
            endpoint: '/user/products',
            queryBuilder: () => ({ userId: user._id }),
            setLoading: setLoading
        });
        // eslint-disable-next-line
    }, [ user, socketReady ])

    // Search labels for a given user and maybe a given product.
    useEffect(() => {
        updateGroup({
            api: api.current,
            requestFn: requestWithReconnect,
            groupName: 'labels',
            setGroup: setLabels,
            setSelected: setLabel,
            guard: socketReady && user?._id,
            endpoint: '/user/labels',
            queryBuilder: () => ({
                ...(product?._id && { productId: product._id }),
                userId: user._id
            }),
            setLoading: setLoading
        });
        // eslint-disable-next-line
    }, [ user, product, socketReady ])

    useEffect(() => {
        if (!user?._id) {
            setMeasurements([]);
            setColumns([]);
            setMeasurement(null);
            return;
        }

        updateMeasurements();
        // eslint-disable-next-line
    }, [ user, product, label, includeInvalid ]);

    useEffect(() => {
        setOpenNotificationJobs({});
        setOpenNotifications({});
        setOpenSurveys({});
    }, [measurement?._id]);

    // Update measurement grid for user (and maybe product + label)
    const updateMeasurements = () => {
        updateGroup({
            api: api.current,
            requestFn: requestWithReconnect,
            groupName: 'measurements',
            setGroup: (measurements) => {
                // This will get called immidietely to reset the state before the websocket call.
                if (!measurements?.length)
                {
                    setMeasurements([]);
                    setColumns([]);

                    return;
                }

                // Show measurements in the order they came in
                measurements.sort((a, b) => b.measurementTimeUTC - a.measurementTimeUTC);

                // Transform nested objects into flat style.
                // This is less than optimal, but it's ok (this is an internal page).
                // Additionally, DataGrid requires an `id` field, so set that here.
                const flattened = measurements.map((measurement, idx) => {
                    const flat = flatten(measurement, '.', true);
                    flat.id = idx;

                    return flat;
                });

                // Get grid column labels
                setColumns(sortedKeys(flattened).map(columnInfo));

                // Save these for display.
                setMeasurements(flattened);
            },
            setSelected: setMeasurement,
            guard: socketReady && user?._id,
            endpoint: '/measurement/find',
            queryBuilder: () => ({
                ...(includeInvalid && { invalid: 'include' }),
                ...(product?._id && { productId: product._id }),
                ...(label?._id && { mac: label._id }),
                userId: user._id,
            }),
            setLoading: setLoading
        });
    };

    // Update notifications for a given product
    useEffect(() => {
        updateGroup({
            api: api.current,
            requestFn: requestWithReconnect,
            groupName: 'notifications',
            setGroup: setNotifications,
            setSelected: () => {},
            guard: socketReady && product?._id,
            endpoint: '/notification/find',
            queryBuilder: () => ({ productId: product._id }),
            setLoading: setLoading
        });
        // eslint-disable-next-line
    }, [ product, socketReady ]);

    useEffect(() => {
        updateGroup({
            api: api.current,
            requestFn: requestWithReconnect,
            groupName: 'jobs',
            setGroup: setNotificationJobs,
            setSelected: () => {},
            guard: socketReady && measurement?._id,
            endpoint: '/notification/findJobs',
            queryBuilder: () => ({ measurement: measurement._id }),
            setLoading: setLoading
        });
        // eslint-disable-next-line
    }, [ measurement, socketReady ]);

    // Update surveys for a given measurement
    useEffect(() => {
        updateGroup({
            api: api.current,
            requestFn: requestWithReconnect,
            groupName: 'surveys',
            setGroup: (surveys) => {
                if (!surveys?.length) {
                    setSurveys([]);
                } else {
                    setSurveys(surveys.reduce((arr, next) => {
                        arr.push.apply(arr, next.responses);
                        return arr;
                    }, []));
                }
            },
            setSelected: () => {},
            guard: socketReady && measurement?._id,
            endpoint: '/survey/find',
            queryBuilder: () => ({ measurementId: measurement?._id }),
            setLoading: setLoading
        });
        // eslint-disable-next-line
    }, [ measurement, socketReady ]);

    const setJobActionLoading = (jobId, value) => {
        setNotificationJobActionLoading((prev) => ({
            ...prev,
            [jobId]: value
        }));
    };

    const getRetriggerOptions = (jobId) => {
        return retriggerOptionsByJob[jobId] || {
            preserveCreatedAt: false,
            refreshLatestPushToken: false,
            sendToAllValidFcmTokens: false
        };
    };

    const updateRetriggerOption = (jobId, key, value) => {
        setRetriggerOptionsByJob((prev) => {
            const current = prev[jobId] || {
                preserveCreatedAt: false,
                refreshLatestPushToken: false,
                sendToAllValidFcmTokens: false
            };

            const next = {
                ...current,
                [key]: value
            };

            if (key === 'sendToAllValidFcmTokens' && value) {
                next.refreshLatestPushToken = false;
            }

            if (key === 'refreshLatestPushToken' && value) {
                next.sendToAllValidFcmTokens = false;
            }

            return {
                ...prev,
                [jobId]: next
            };
        });
    };

    const refreshMeasurementDetails = async () => {
        if (!measurement?._id) {
            return;
        }

        try {
            setLoading(true);

            const [ jobsRes, notificationsRes, surveysRes ] = await Promise.all([
                requestWithReconnect('/notification/findJobs', { measurement: measurement._id }),
                product?._id
                    ? requestWithReconnect('/notification/find', { productId: product._id })
                    : Promise.resolve({ notifications: [] }),
                requestWithReconnect('/survey/find', { measurementId: measurement._id })
            ]);

            if (!jobsRes?.error) {
                setNotificationJobs(jobsRes.jobs || []);
            }

            if (!notificationsRes?.error) {
                setNotifications(notificationsRes.notifications || []);
            }

            if (!surveysRes?.error) {
                const nextSurveys = !surveysRes?.surveys?.length
                    ? []
                    : surveysRes.surveys.reduce((arr, next) => {
                        arr.push.apply(arr, next.responses);
                        return arr;
                    }, []);

                setSurveys(nextSurveys);
            }

            if (user?._id) {
                const measurementRes = await requestWithReconnect('/measurement/find', {
                    ...(includeInvalid && { invalid: 'include' }),
                    ...(product?._id && { productId: product._id }),
                    ...(label?._id && { mac: label._id }),
                    userId: user._id,
                });

                if (!measurementRes?.error && Array.isArray(measurementRes.measurements)) {
                    const rawSelectedMeasurement = measurementRes.measurements.find(
                        (entry) => `${entry._id}` === `${measurement._id}`
                    );

                    if (rawSelectedMeasurement) {
                        const flatSelectedMeasurement = flatten(rawSelectedMeasurement, '.', true);

                        const existingGridRow = measurements.find(
                            (entry) => `${entry._id}` === `${rawSelectedMeasurement._id}`
                        );

                        flatSelectedMeasurement.id = existingGridRow?.id ?? flatSelectedMeasurement._id;
                        setMeasurement(flatSelectedMeasurement);
                    }
                }
            }
        } catch (err) {
            console.error('Failed to refresh measurement details:', err);
            window.alert('Failed to refresh measurement details.');
        } finally {
            setLoading(false);
        }
    };

    const refreshNotificationJobs = () => {
        updateGroup({
            api: api.current,
            requestFn: requestWithReconnect,
            groupName: 'jobs',
            setGroup: setNotificationJobs,
            setSelected: () => {},
            guard: measurement?._id,
            endpoint: '/notification/findJobs',
            queryBuilder: () => ({ measurement: measurement._id }),
            setLoading: setLoading
        });
    };

    const failNotificationJob = async (jobId) => {
        try {
            setJobActionLoading(jobId, true);

            const res = await requestWithReconnect('/notification/failJob', { id: jobId });

            if (!res?.ok) {
                window.alert(`Failed to fail notification job${res?.error ? `: ${res.error}` : ''}`);
                return;
            }

            refreshNotificationJobs();
        } catch (err) {
            console.error('Fail notification job error:', err);
            window.alert('Failed to fail notification job');
        } finally {
            setJobActionLoading(jobId, false);
        }
    };

    const waitNotificationJob = async (jobId) => {
        try {
            setJobActionLoading(jobId, true);

            const res = await requestWithReconnect('/notification/waitJob', { id: jobId });

            if (!res?.ok) {
                window.alert(`Failed to retry notification job${res?.error ? `: ${res.error}` : ''}`);
                return;
            }

            refreshNotificationJobs();
        } catch (err) {
            console.error('Wait notification job error:', err);
            window.alert('Failed to retry notification job');
        } finally {
            setJobActionLoading(jobId, false);
        }
    };

    const retriggerNotificationJob = async (jobId) => {
        try {
            setJobActionLoading(jobId, true);

            const options = getRetriggerOptions(jobId);

            const res = await requestWithReconnect('/notification/retriggerJob', {
                id: jobId,
                preserveCreatedAt: !!options.preserveCreatedAt,
                refreshLatestPushToken: !!options.refreshLatestPushToken,
                sendToAllValidFcmTokens: !!options.sendToAllValidFcmTokens
            });

            if (!res?.ok) {
                window.alert(`Failed to retrigger notification job${res?.error ? `: ${res.error}` : ''}`);
                return;
            }

            refreshNotificationJobs();
        } catch (err) {
            console.error('Retrigger notification job error:', err);
            window.alert('Failed to retrigger notification job');
        } finally {
            setJobActionLoading(jobId, false);
        }
    };

    const expandAllNotificationJobs = () => {
        setAllMapValues(notificationJobs, setOpenNotificationJobs, true);
    };

    const collapseAllNotificationJobs = () => {
        setAllMapValues(notificationJobs, setOpenNotificationJobs, false);
    };

    const expandAllNotifications = () => {
        setAllMapValues(notifications, setOpenNotifications, true);
    };

    const collapseAllNotifications = () => {
        setAllMapValues(notifications, setOpenNotifications, false);
    };

    // Style generator for lists.
    const listStyle = {
        backgroundColor: 'white',
        maxHeight: '25em',
        maxWidth: '25em',
        overflowY: 'auto',
        height: '100%',
        width: '100%'
    };

    // This has to happen inside the component definition.
    const gridStyle = makeStyles({
        forcedRow: {
            backgroundColor: '#fed7aa'
        },
        invalidRow: {
            backgroundColor: '#fecaca'
        },
        measurementHeartbeatRow: {
            backgroundColor: '#fbcfe8'
        },
        measurementForcedRow: {
            backgroundColor: '#fed7aa'
        },
        measurementTiltRow: {
            backgroundColor: '#bbf7d0'
        },
        measurementQaRow: {
            backgroundColor: '#bfdbfe'
        },
        measurementOtherTypeRow: {
            backgroundColor: '#e9d5ff'
        },
        selectedMeasurementRow: {
            backgroundColor: '#cbd5e1 !important'
        },
        pageLayout: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: 16,
            width: '100%'
        },
        mainContent: {
            flex: 1,
            minWidth: 0
        },
        sidePanel: {
            width: 420,
            minWidth: 420,
            maxWidth: 420,
            position: 'sticky',
            top: 24,
            alignSelf: 'flex-start'
        },
        sidePanelBody: {
            maxHeight: 'calc(100vh - 120px)',
            overflowY: 'auto',
            padding: 16,
            paddingRight: 20
        },
        sectionPaper: {
            marginBottom: 12
        }
    })();

    const classes = withStyle();

    // Render an object in a list.
    const renderObject = (obj, array = false) => {
        // It's oddly difficult to make list primary/secondary text monospace.
        const textStyle = { fontFamily: 'Monospace' };

        // Render value text depending on what `value` actually is.
        // Note that we assume the value is part of a flattened object,
        //   so it may be an array but not an object.
        const renderValue = (value) => {
            if (value) {
                if (Array.isArray(value)) {
                    return (<>{value.map((entry) => (<div>{
                        (typeof entry) !== 'object' ? entry : JSON.stringify(entry)
                    }</div>))}</>);
                } else {
                    return value.toString();
                }
            } else if (value === '') {
                return '{EMPTY STRING}';
            } else if (value === null) {
                return '{NULL}';
            } else if (value === undefined) {
                return '{UNDEFINED}';
            } else {
                return value.toString();
            }
        };

        return (<List dense disablePadding style={listStyle} >
            {Object.entries(flatten(obj, '.', array)).map(([ key, value ]) => (
                <ListItem key={key}>
                    <ListItemText primary={<div style={textStyle}>{key}</div>} secondary={
                        <div style={textStyle}>{renderValue(value)}
                    </div>} />
                </ListItem>
            ))}
        </List>);
    };

    const renderCollection = ({
        collection,
        type,
        primary,
        secondary,
        select,
        selected,
        renderSelected,
        sectionOpen,
        setSectionOpen,
        selectedSectionOpen,
        setSelectedSectionOpen
    }) => {
        if (!collection.length) { return null; }

        return (
            <>
                <Grid item>
                    <Paper style={{ paddingTop: 8 }} elevation={3}>
                        <Grid container alignItems="center" style={softHeaderStyle}>
                            <Grid item xs>
                                <Typography variant="subtitle1">{`${type}s`}</Typography>
                            </Grid>
                            <Grid item>
                                <IconButton size="small" onClick={() => setSectionOpen(!sectionOpen)}>
                                    {sectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                            </Grid>
                            <Grid item style={{ marginTop: -8 }}>
                                <Button color="primary" onClick={() => select(null)}>
                                    <CancelIcon />
                                </Button>
                            </Grid>
                        </Grid>
                        <Divider />
                        <Collapse in={sectionOpen}>
                            <List dense disablePadding style={listStyle}>
                                {collection.map((item) => (
                                    <ListItem
                                        button
                                        key={item._id}
                                        selected={item._id === selected?._id}
                                        onClick={() => select(item)}
                                    >
                                        <ListItemText
                                            primary={item[primary]}
                                            secondary={secondary ? item[secondary] : ''}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Collapse>
                    </Paper>
                </Grid>

                {(renderSelected && !!selected) ? (
                    <Grid item>
                        <Paper style={{ paddingTop: 8 }} elevation={3}>
                            <Grid container alignItems="center" style={softHeaderStyle}>
                                <Grid item xs>
                                    <Typography variant="subtitle1">{`Selected ${type}`}</Typography>
                                </Grid>
                                <Grid item>
                                    <IconButton size="small" onClick={() => setSelectedSectionOpen(!selectedSectionOpen)}>
                                        {selectedSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                </Grid>
                            </Grid>
                            <Divider />
                            <Collapse in={selectedSectionOpen}>
                                {renderObject(selected)}
                            </Collapse>
                        </Paper>
                    </Grid>
                ) : null}
            </>
        );
    };

    const renderTopRow = () => {
        if (!users?.length && !products?.length && !labels?.length) {
            return null;
        }

        return (
            <Grid container spacing={3}>
                {renderCollection({
                    collection: users,
                    type: 'User',
                    primary: 'email',
                    secondary: '_id',
                    select: setUser,
                    selected: user,
                    renderSelected: true,
                    sectionOpen: usersSectionOpen,
                    setSectionOpen: setUsersSectionOpen,
                    selectedSectionOpen: selectedUserSectionOpen,
                    setSelectedSectionOpen: setSelectedUserSectionOpen
                })}

                {renderCollection({
                    collection: products,
                    type: 'Product',
                    primary: 'name',
                    secondary: '_id',
                    select: setProduct,
                    selected: product,
                    renderSelected: true,
                    sectionOpen: productsSectionOpen,
                    setSectionOpen: setProductsSectionOpen,
                    selectedSectionOpen: selectedProductSectionOpen,
                    setSelectedSectionOpen: setSelectedProductSectionOpen
                })}

                {renderCollection({
                    collection: labels,
                    type: 'Label',
                    primary: '_id',
                    secondary: '',
                    select: setLabel,
                    selected: label,
                    renderSelected: false,
                    sectionOpen: labelsSectionOpen,
                    setSectionOpen: setLabelsSectionOpen
                })}
            </Grid>
        );
    };

    const getMeasurementTypeRowClass = (row) => {
        if (!colorByMeasurementType) {
            return '';
        }

        const type = (row['data.measurementType'] || '').toString().toLowerCase();
        const boardRevision = (row['data.boardRevision'] || '').toString().toUpperCase();

        if (type === 'heartbeat') {
            return gridStyle.measurementHeartbeatRow;
        }

        if (type === 'forced' || row['data.forced']) {
            return gridStyle.measurementForcedRow;
        }

        if (type === 'tilt') {
            return gridStyle.measurementTiltRow;
        }

        // criteria for QA measurements
        if (boardRevision === 'CCRA') {
            return gridStyle.measurementQaRow;
        }

        if (type) {
            return gridStyle.measurementOtherTypeRow;
        }

        return '';
    };

    const renderMeasurementTypeLegend = () => (
        <div style={{ padding: 4, minWidth: 220 }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ width: 14, height: 14, backgroundColor: '#fbcfe8', border: '1px solid #94a3b8', marginRight: 8 }} />
                <Typography variant="body2">Heartbeat</Typography>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ width: 14, height: 14, backgroundColor: '#fed7aa', border: '1px solid #94a3b8', marginRight: 8 }} />
                <Typography variant="body2">Forced</Typography>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ width: 14, height: 14, backgroundColor: '#bbf7d0', border: '1px solid #94a3b8', marginRight: 8 }} />
                <Typography variant="body2">Tilt</Typography>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ width: 14, height: 14, backgroundColor: '#bfdbfe', border: '1px solid #94a3b8', marginRight: 8 }} />
                <Typography variant="body2">QA</Typography>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ width: 14, height: 14, backgroundColor: '#fecaca', border: '1px solid #94a3b8', marginRight: 8 }} />
                <Typography variant="body2">Invalid</Typography>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ width: 14, height: 14, backgroundColor: '#e9d5ff', border: '1px solid #94a3b8', marginRight: 8 }} />
                <Typography variant="body2">Other measurement type</Typography>
            </div>
        </div>
    );

    const renderMeasurementGrid = () => {
        if (!measurements.length) { return null; }

        return (
            <Paper style={{ marginTop: 12, display: 'flex', flexDirection: 'column' }} elevation={3}>
                <Grid container alignItems="center" style={{ padding: 12, paddingBottom: 4 }}>
                    <Grid item>
                        <FormGroup row>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="secondary"
                                        checked={colorByMeasurementType}
                                        onChange={() => setColorByMeasurementType(!colorByMeasurementType)}
                                    />
                                }
                                label={
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography component="span">Color by measurement type</Typography>
                                        <Tooltip
                                            title={renderMeasurementTypeLegend()}
                                            interactive
                                            arrow
                                            placement="right"
                                        >
                                            <InfoOutlinedIcon
                                                fontSize="small"
                                                style={{ marginLeft: 6, color: '#64748b', cursor: 'pointer' }}
                                            />
                                        </Tooltip>
                                    </div>
                                }
                            />
                        </FormGroup>
                    </Grid>
                </Grid>

                <DataGrid
                    rows={measurements}
                    columns={columns}
                    rowCount={measurements.length}
                    autoHeight
                    selectionModel={selectedMeasurementRowId !== null ? [selectedMeasurementRowId] : []}
                    onSelectionModelChange={(selection) => {
                        if (selection?.length !== 1)
                        {
                            setMeasurement(null);
                            setSelectedMeasurementRowId(null);
                            return;
                        }

                        const selected = measurements.filter(({ id }) => id === selection[0]);

                        if (selected?.length !== 1)
                        {
                            console.warn('Warning: Attempt to select unknown row:', selection[0]);
                            setMeasurement(null);
                            setSelectedMeasurementRowId(null);
                            return;
                        }

                        setMeasurement(selected[0] ?? null);
                        setSelectedMeasurementRowId(selection[0]);
                        console.info('Selected', selected[0]);
                    }}
                    getRowClassName={(params) => {
                        const classes = [];

                        if (params.row.id === selectedMeasurementRowId) {
                            classes.push(gridStyle.selectedMeasurementRow);
                        }

                        if (params.row['invalid']) {
                            classes.push(gridStyle.invalidRow);
                        } else if (colorByMeasurementType) {
                            const measurementTypeClass = getMeasurementTypeRowClass(params.row);
                            if (measurementTypeClass) {
                                classes.push(measurementTypeClass);
                            }
                        }

                        return classes.join(' ');
                    }}
                />
            </Paper>
        );
    };

    const toggleMapValue = (setter, key) => {
        setter((prev) => ({
            ...prev,
            [key]: !(prev[key] ?? true)
        }));
    };

    const setAllMapValues = (items, setter, isOpen) => {
        const next = {};

        items.forEach((item) => {
            next[item._id] = isOpen;
        });

        setter(next);
    };

    const softHeaderStyle = {
        backgroundColor: '#eef2f7',
        color: '#334155',
        paddingLeft: 8,
        paddingRight: 4,
        paddingTop: 6,
        paddingBottom: 6
    };

    const softSubHeaderStyle = {
        backgroundColor: '#f8fafc',
        color: '#475569',
        paddingLeft: 8,
        paddingRight: 4,
        paddingTop: 4,
        paddingBottom: 4
    };

    const renderCollapsibleHeader = ({ title, open, onToggle, style = {}, actions = null }) => (
        <Grid container alignItems="center" style={{ ...softHeaderStyle, ...style }}>
            <Grid item xs>
                <Typography variant="subtitle1">{title}</Typography>
            </Grid>

            {actions ? <Grid item>{actions}</Grid> : null}

            <Grid item>
                <IconButton size="small" onClick={onToggle}>
                    {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Grid>
        </Grid>
    );

    const renderCollapsiblePaper = ({ title, open, onToggle, children, headerStyle = {}, actions = null }) => (
        <Paper elevation={3} className={gridStyle.sectionPaper}>
            {renderCollapsibleHeader({ title, open, onToggle, style: headerStyle, actions })}
            <Divider />
            <Collapse in={open}>
                {children}
            </Collapse>
        </Paper>
    );

    const renderNotificationJobsSection = () => {
        if (!notificationJobs.length) { return null; }

        const headerColorForJob = (job) => {
            if (job.failed) {
                return '#fee2e2';
            }

            if (job.completed) {
                return '#dcfce7';
            }

            if (job.waiting) {
                return '#fef3c7';
            }

            return '#fed7aa';
        };

        return renderCollapsiblePaper({
            title: 'Notification Jobs',
            open: notificationJobsSectionOpen,
            onToggle: () => setNotificationJobsSectionOpen(!notificationJobsSectionOpen),
            actions: (
                <div style={{ display: 'flex', alignItems: 'center', marginRight: 4 }}>
                    <IconButton
                        size="small"
                        onClick={expandAllNotificationJobs}
                        title="Expand all"
                    >
                        <UnfoldMoreIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={collapseAllNotificationJobs}
                        title="Collapse all"
                    >
                        <UnfoldLessIcon fontSize="small" />
                    </IconButton>
                </div>
            ),
            children: (
                <div style={{ padding: 8 }}>
                    {notificationJobs.map((job) => {
                        const key = job._id;
                        const isOpen = openNotificationJobs[key] ?? true;

                        return (
                            <Paper key={job._id} elevation={1} style={{ marginBottom: 12 }}>
                                <Grid container alignItems="center" style={{ ...softSubHeaderStyle, backgroundColor: headerColorForJob(job) }}>
                                    <Grid item xs>
                                        <Typography variant="subtitle2">{job._id}</Typography>
                                    </Grid>
                                    <Grid item>
                                        <IconButton
                                            size="small"
                                            onClick={() => toggleMapValue(setOpenNotificationJobs, key)}
                                        >
                                            {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        </IconButton>
                                    </Grid>
                                </Grid>
                                <Divider />
                                <Collapse in={isOpen}>
                                    {renderObject(job)}
                                    {renderNotificationJobActions(job)}
                                </Collapse>
                            </Paper>
                        );
                    })}
                </div>
            )
        });
    };

    const renderNotificationJobActions = (job) => {
        const options = getRetriggerOptions(job._id);
        const actionLoading = !!notificationJobActionLoading[job._id];
        const isFailed = job.failed === true;
        const isWaiting = job.waiting === true;
        const isCompleted = job.completed === true;
        const isPush = job.notificationType === 'push';

        return (
            <div style={{ padding: 8 }}>
                <Divider style={{ marginBottom: 8 }} />
                <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                    Actions
                </Typography>

                <Grid container spacing={1} style={{ marginBottom: 8 }}>
                    {isFailed && (
                        <Grid item>
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={actionLoading}
                                onClick={() => waitNotificationJob(job._id)}
                            >
                                Retry Existing Job
                            </Button>
                        </Grid>
                    )}

                    {isWaiting && (
                        <Grid item>
                            <Button
                                variant="contained"
                                color="secondary"
                                disabled={actionLoading}
                                onClick={() => failNotificationJob(job._id)}
                            >
                                Fail Waiting Job
                            </Button>
                        </Grid>
                    )}

                    {(isFailed || isCompleted) && (
                        <Grid item>
                            <Button
                                variant="contained"
                                style={{ backgroundColor: '#e2e8f0' }}
                                disabled={actionLoading}
                                onClick={() => retriggerNotificationJob(job._id)}
                            >
                                Queue Retrigger
                            </Button>
                        </Grid>
                    )}
                </Grid>

                {(isFailed || isCompleted) && (
                    <Paper variant="outlined" style={{ padding: 8, backgroundColor: '#f8fafc' }}>
                        <Typography variant="body2" style={{ marginBottom: 4 }}>
                            Retrigger Options
                        </Typography>

                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="primary"
                                        checked={!!options.preserveCreatedAt}
                                        onChange={(event) => {
                                            updateRetriggerOption(job._id, 'preserveCreatedAt', event.target.checked);
                                        }}
                                    />
                                }
                                label="Preserve original createdAt"
                            />

                            {isPush && (
                                <>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                color="primary"
                                                checked={!!options.refreshLatestPushToken}
                                                onChange={(event) => {
                                                    updateRetriggerOption(job._id, 'refreshLatestPushToken', event.target.checked);
                                                }}
                                            />
                                        }
                                        label="Append latest valid FCM token"
                                    />

                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                color="primary"
                                                checked={!!options.sendToAllValidFcmTokens}
                                                onChange={(event) => {
                                                    updateRetriggerOption(job._id, 'sendToAllValidFcmTokens', event.target.checked);
                                                }}
                                            />
                                        }
                                        label="Append all valid FCM tokens"
                                    />
                                </>
                            )}
                        </FormGroup>
                    </Paper>
                )}
            </div>
        );
    };

    const renderNotificationsSection = () => {
        if (!notifications.length) { return null; }

        return renderCollapsiblePaper({
            title: 'Notifications',
            open: notificationsSectionOpen,
            onToggle: () => setNotificationsSectionOpen(!notificationsSectionOpen),
            actions: (
                <div style={{ display: 'flex', alignItems: 'center', marginRight: 4 }}>
                    <IconButton
                        size="small"
                        onClick={expandAllNotifications}
                        title="Expand all"
                    >
                        <UnfoldMoreIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={collapseAllNotifications}
                        title="Collapse all"
                    >
                        <UnfoldLessIcon fontSize="small" />
                    </IconButton>
                </div>
            ),
            children: (
                <div style={{ padding: 8 }}>
                    {notifications.map((notification) => {
                        const key = notification._id;
                        const isOpen = openNotifications[key] ?? true;

                        return (
                            <Paper key={notification._id} elevation={1} style={{ marginBottom: 12 }}>
                                <Grid 
                                    container 
                                    alignItems="center" 
                                    style={{
                                        ...softSubHeaderStyle,
                                        backgroundColor: notification.active ? '#dcfce7' : '#fee2e2'
                                    }}>
                                    <Grid item xs>
                                        <Typography variant="subtitle2">
                                            {notification?.trigger?.type || notification._id}
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <IconButton
                                            size="small"
                                            onClick={() => toggleMapValue(setOpenNotifications, key)}
                                        >
                                            {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        </IconButton>
                                    </Grid>
                                </Grid>
                                <Divider />
                                <Collapse in={isOpen}>
                                    {renderObject(notification)}
                                </Collapse>
                            </Paper>
                        );
                    })}
                </div>
            )
        });
    };

    const renderSurveysSection = () => {
        if (!surveys.length) { return null; }

        return renderCollapsiblePaper({
            title: 'Surveys',
            open: surveysSectionOpen,
            onToggle: () => setSurveysSectionOpen(!surveysSectionOpen),
            children: (
                <div style={{ padding: 8 }}>
                    {surveys.map((survey, idx) => {
                        const key = `${survey?.surveyId || 'survey'}-${idx}`;
                        const isOpen = openSurveys[key] ?? true;

                        return (
                            <Paper key={key} elevation={1} style={{ marginBottom: 12 }}>
                                <Grid container alignItems="center" style={softSubHeaderStyle}>
                                    <Grid item xs>
                                        <Typography variant="subtitle2">{survey?.surveyId}</Typography>
                                    </Grid>
                                    <Grid item>
                                        <IconButton
                                            size="small"
                                            onClick={() => toggleMapValue(setOpenSurveys, key)}
                                        >
                                            {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        </IconButton>
                                    </Grid>
                                </Grid>
                                <Divider />
                                <Collapse in={isOpen}>
                                    {renderObject(survey, true)}
                                </Collapse>
                            </Paper>
                        );
                    })}
                </div>
            )
        });
    };

    const renderSidePanel = () => {
        return (
            <Paper elevation={3} className={gridStyle.sidePanel}>
                <Grid container alignItems="center" style={{ ...softHeaderStyle, paddingTop: 8, paddingBottom: 8 }}>
                    <Grid item>
                        <Typography variant="h6">Measurement Details</Typography>
                    </Grid>

                    <Grid item style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                        <IconButton
                            size="small"
                            onClick={refreshMeasurementDetails}
                            disabled={!measurement || loading}
                            title="Refresh measurement details"
                        >
                            <RefreshIcon />
                        </IconButton>

                        <Button
                            color="primary"
                            onClick={() => {
                                setMeasurement(null);
                                setSelectedMeasurementRowId(null);
                            }}
                        >
                            <CancelIcon />
                        </Button>
                    </Grid>
                </Grid>
                <Divider />
                <div className={gridStyle.sidePanelBody}>
                    {!measurement ? (
                        <Typography variant="body2" style={{ padding: 8 }}>
                            Select a measurement to view its details, notification jobs, and surveys.
                        </Typography>
                    ) : (
                        <>
                            {renderCollapsiblePaper({
                                title: 'Selected Measurement',
                                open: measurementSectionOpen,
                                onToggle: () => setMeasurementSectionOpen(!measurementSectionOpen),
                                children: renderObject(measurement, true)
                            })}
                            {renderNotificationJobsSection()}
                            {renderNotificationsSection()}
                            {renderSurveysSection()}
                        </>
                    )}
                </div>
            </Paper>
        );
    };

    const renderContent = () => {
        if (blocked) {
            return <div></div>;
        }

        const textFieldKeyDown = (event) => {
            if (event.key === 'Enter')
            {
                event.preventDefault();
                searchUsers()
            }
        };

        return (
            <div className={gridStyle.pageLayout}>
                <div className={gridStyle.mainContent}>
                    <Grid container spacing={3} alignItems="center">
                        <Grid item>
                            <Grid container spacing={3}>
                                <Grid item>
                                    <TextField
                                        label="email"
                                        defaultValue=""
                                        onChange={(event) => {
                                            setUserQuery({ ...userQuery, email: event.target.value });
                                        }}
                                        onKeyDown={textFieldKeyDown}
                                        autoFocus
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        label="id"
                                        defaultValue=""
                                        onChange={(event) => {
                                            setUserQuery({ ...userQuery, userId: event.target.value });
                                        }}
                                        onKeyDown={textFieldKeyDown}
                                    />
                                </Grid>
                                <Grid item style={{ marginTop: 5 }}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        style={{ marginRight: 5 }}
                                        onClick={searchUsers}
                                    >
                                        Search
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        disabled={user === null}
                                        onClick={updateMeasurements}
                                    >
                                        Get Measurements
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <FormGroup row style={{ marginTop: 2 }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    color="secondary"
                                                    checked={includeInvalid}
                                                    onChange={() => setIncludeInvalid(!includeInvalid)}
                                                />
                                            }
                                            label={<Typography component="span">Include Invalid</Typography>}
                                        />
                                    </FormGroup>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item style={{ marginLeft: 'auto' }}>
                            <Button
                                color="secondary"
                                onClick={() => {
                                    // This function literally resets the entire state.
                                    // I wish there was a better way to do this, but alas.
                                    setUserQuery({ email: '', userId: '' });
                                    setIncludeInvalid(false);

                                    setUser(null);
                                    setUsers([]);

                                    setProduct(null);
                                    setProducts([]);

                                    setLabel(null);
                                    setLabels([]);

                                    setColumns([]);

                                    setMeasurement(null);
                                    setMeasurements([]);
                                    setSelectedMeasurementRowId(null);

                                    setNotificationJobs([]);
                                    setNotifications([]);

                                    setSurveys([]);
                                }}
                            >
                                Clear All
                            </Button>
                        </Grid>
                    </Grid>

                    {loading ? (
                        <LinearProgress color="secondary" style={{ marginTop: 5, marginBottom: 5 }} />
                    ) : null}

                    {renderTopRow()}
                    {renderMeasurementGrid()}
                </div>

                {renderSidePanel()}
            </div>
        );
    };

    return (<div class={classes.root}>
        <CssBaseline />
        <AppContainer classes={classes} title="User Info" match={match}>
            <div className={classes.appBarSpacer} />
            {renderContent()}
            <div style={{ paddingTop: '50px' }}>
                <MadeWithLove />
            </div>
        </AppContainer>
    </div>);
};
